/**
 * The project-icon join, as one hook.
 *
 * Surfaces that draw a project (breadcrumb, command palette, command centre
 * task rows, the new-thread landing) hold a project's ids and little else. Each
 * one subscribing to CMMD's records itself and rebuilding the maps is how the
 * letter-avatar bug came back per surface, so they share this instead.
 *
 * Pass the project id wherever the surface has one. Without it only the cloud
 * runtime can answer, and a local checkout linked to a CMMD project falls back
 * to whatever favicon is committed in that checkout — which is the identity
 * CMMD's record is supposed to override.
 */

import { useMemo } from "react";

import { useCmmdForgeRuntimeListSnapshot } from "~/cmmd/cmmdForgeRuntimeListResource";
import { useProjectLinkListSnapshot } from "~/cmmd/projectLinkListResource";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { useSavedEnvironmentRegistryStore } from "~/environments/runtime";
import type { ProjectCanonicalIdentity } from "../ProjectIcon";
import {
  buildProjectIconByProjectKey,
  buildProjectIconByRuntimeId,
  resolveProjectCanonicalIcon,
} from "./projectIconSource";

/**
 * Returns a stable resolver: a project's ids → its CMMD identity, or
 * `undefined` when no CMMD record answers and the checkout favicon should be
 * tried instead.
 */
export function useProjectIconResolver(): (
  environmentId: string | null | undefined,
  projectId?: string | null | undefined,
) => ProjectCanonicalIdentity | undefined {
  const runtimeSnapshot = useCmmdForgeRuntimeListSnapshot();
  const linkSnapshot = useProjectLinkListSnapshot();
  const intakeSnapshot = useCmmdWorkIntakeSnapshot();
  const savedEnvironments = useSavedEnvironmentRegistryStore((state) => state.byId);

  const iconByRuntimeId = useMemo(
    () => buildProjectIconByRuntimeId(runtimeSnapshot.rows),
    [runtimeSnapshot.rows],
  );
  const iconByProjectKey = useMemo(
    () => buildProjectIconByProjectKey(linkSnapshot.links, intakeSnapshot.projects),
    [linkSnapshot.links, intakeSnapshot.projects],
  );

  return useMemo(
    () => (environmentId, projectId) =>
      environmentId === null || environmentId === undefined
        ? undefined
        : resolveProjectCanonicalIcon(
            environmentId,
            iconByRuntimeId,
            projectId,
            iconByProjectKey,
            savedEnvironments,
          ),
    [iconByProjectKey, iconByRuntimeId, savedEnvironments],
  );
}
